import { provideHttpClient } from '@angular/common/http';
import { ApplicationConfig, EnvironmentInjector, ErrorHandler, Injector, inject, provideAppInitializer, runInInjectionContext } from '@angular/core';
import { DOCUMENT_CONTENT_CSS } from '@aveespa/common/constants/document-content.constants';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { FirebasePermissionErrorHandler } from 'app/@core/services/utility/firestore-permission-error-handler.service';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { connectFirestoreEmulator, getFirestore, provideFirestore } from '@angular/fire/firestore';

import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideFuse } from '@fuse';
import { provideAuth } from 'app/@core/auth/auth.provider';
import { provideIcons } from 'app/@core/icons/icons.provider';
import { appRoutes } from 'app/app.routes';
import { MockApiService } from 'app/mock-api';


import { connectFunctionsEmulator, getFunctions, provideFunctions } from '@angular/fire/functions';
import { connectStorageEmulator, getStorage, provideStorage } from '@angular/fire/storage';
import { environment } from 'environments/environment';
import { getEmulatorHost } from './@core/utils/emulator-host';


export const appConfig: ApplicationConfig = {
    providers: [
        // Styles imported/edited PDF document content (Quill editor, PDF preview) — the exact
        // same stylesheet also gets embedded server-side when the PDF itself is generated
        // (see functions/src/core/utils/pdf.utils.ts), so what the admin sees while editing
        // matches what ends up in the final document instead of silently falling back to
        // Chromium's bare defaults there.
        provideAppInitializer(() => {
            const styleEl = document.createElement('style');
            styleEl.textContent = DOCUMENT_CONTENT_CSS;
            document.head.appendChild(styleEl);
        }),

        // Global error handler — intercepts Firebase permission-denied errors
        // caused by tenant suspension and redirects to /suspended.
        {
            provide: ErrorHandler,
            useFactory: (injector: Injector) => new FirebasePermissionErrorHandler(injector),
            deps: [Injector],
        },

        //Firebase initialization
        provideFirebaseApp(() =>
            initializeApp(environment.firebase)
        ),
        provideFirestore(() => {
            const firestore = getFirestore();
            if (environment.useEmulators) {
                // getEmulatorHost() resolves asynchronously, so the .then callback
                // loses Angular's injection context — restore it explicitly.
                const injector = inject(EnvironmentInjector);
                getEmulatorHost().then(host => {
                    runInInjectionContext(injector, () => {
                        connectFirestoreEmulator(firestore, host, environment.emulators.firestore.port);
                    });
                });
            }
            return firestore;
        }),
        provideFunctions(() => {
            const functions = getFunctions();
            if (environment.useEmulators) {
                const injector = inject(EnvironmentInjector);
                getEmulatorHost().then(host => {
                    runInInjectionContext(injector, () => {
                        connectFunctionsEmulator(functions, host, environment.emulators.functions.port);
                    });
                });
            }
            return functions;
        }),
        provideStorage(() => {
            const storage = getStorage();
            if (environment.useEmulators) {
                const injector = inject(EnvironmentInjector);
                getEmulatorHost().then(host => {
                    runInInjectionContext(injector, () => {
                        connectStorageEmulator(storage, host, environment.emulators.storage.port);
                    });
                });
            }
            return storage;
        }),

        //Angular & Router
        provideAnimations(),
        provideHttpClient(),
        provideRouter(
            appRoutes,
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // Global form field defaults
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'fill' } },

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: ['D', 'MMddyyyy'],
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            // Remove this to go to production.
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: 'dense',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-default',
                themes: [],
            },
        }),

    ],
};
